import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import {
  UserPlus, Loader2, Trash2, Shield, ShieldCheck, Users, Eye, EyeOff, Edit3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function SAAdmins() {
  const { sa: me, refresh } = useSuperAdminAuth();
  const { tenants } = useSuperAdminTenant();
  const [rows, setRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', row?: {...} }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get("/admins");
      setRows(data.items || []);
      setFeatures(data.features_catalog || []);
      setIsMaster(!!data.is_master);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doDelete = async (r) => {
    if (!window.confirm(`Delete super admin ${r.email}?`)) return;
    try { await saApi.delete(`/admins/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <SuperAdminShell
      title="Super Admins"
      actions={isMaster && (
        <Button data-testid="sa-admins-create" onClick={() => setDialog({ mode: "create" })}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          <UserPlus className="w-4 h-4 mr-1.5" />New Super Admin
        </Button>
      )}
    >
      <div className="px-5 md:px-8 py-6 space-y-4">
        {!isMaster && (
          <div className="rounded-xl px-4 py-3 border border-amber-400/20 bg-amber-400/10 text-amber-200 text-sm">
            You have read-only access. Only Master super admins can add or edit others.
          </div>
        )}
        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Tenants</th>
                <th className="px-4 py-3">Features</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40"><Users className="w-6 h-6 mx-auto mb-2 opacity-40" />No super admins yet</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid="sa-admin-row" className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white">{r.name}</td>
                  <td className="px-4 py-2 text-white/80">{r.email}</td>
                  <td className="px-4 py-2">
                    {r.is_master ? (
                      <Badge className="bg-cyan-500/15 text-cyan-300 border-0"><ShieldCheck className="w-3 h-3 mr-1 inline" />Master</Badge>
                    ) : (
                      <Badge className="bg-white/10 text-white/70 border-0"><Shield className="w-3 h-3 mr-1 inline" />Admin</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2 text-white/60 text-xs">
                    {(r.allowed_tenant_ids || []).length === 0 ? "All" : `${r.allowed_tenant_ids.length} scoped`}
                  </td>
                  <td className="px-4 py-2 text-white/60 text-xs">
                    {(r.allowed_features || []).length === 0 ? "All" : `${r.allowed_features.length} scoped`}
                  </td>
                  <td className="px-4 py-2">
                    {r.is_active
                      ? <Badge className="bg-emerald-500/15 text-emerald-300 border-0">Active</Badge>
                      : <Badge className="bg-white/10 text-white/50 border-0">Disabled</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isMaster && (
                      <>
                        <Button data-testid={`sa-admin-edit-${r.id}`} size="sm" variant="ghost"
                                onClick={() => setDialog({ mode: "edit", row: r })}
                                className="text-white/70 hover:text-cyan-300"><Edit3 className="w-4 h-4" /></Button>
                        {r.id !== me?.id && (
                          <Button data-testid={`sa-admin-delete-${r.id}`} size="sm" variant="ghost"
                                  onClick={() => doDelete(r)}
                                  className="text-white/70 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && (
        <SAAdminDialog
          mode={dialog.mode}
          row={dialog.row}
          featuresCatalog={features}
          tenants={tenants}
          isMe={dialog.row?.id === me?.id}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); refresh(); }}
        />
      )}
    </SuperAdminShell>
  );
}

function SAAdminDialog({ mode, row, featuresCatalog, tenants, isMe, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    name: row?.name || "",
    email: row?.email || "",
    password: "",
    is_master: !!row?.is_master,
    is_active: row?.is_active ?? true,
    allowed_tenant_ids: row?.allowed_tenant_ids || [],
    allowed_features: row?.allowed_features || [],
  }));
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleTenant = (id) => setForm((f) => ({
    ...f,
    allowed_tenant_ids: f.allowed_tenant_ids.includes(id)
      ? f.allowed_tenant_ids.filter((x) => x !== id)
      : [...f.allowed_tenant_ids, id],
  }));
  const toggleFeature = (k) => setForm((f) => ({
    ...f,
    allowed_features: f.allowed_features.includes(k)
      ? f.allowed_features.filter((x) => x !== k)
      : [...f.allowed_features, k],
  }));

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const patch = {
          name: form.name,
          allowed_tenant_ids: form.allowed_tenant_ids,
          allowed_features: form.allowed_features,
          is_master: form.is_master,
          is_active: form.is_active,
        };
        if (form.password) patch.password = form.password;
        await saApi.put(`/admins/${row.id}`, patch);
        toast.success("Super admin updated");
      } else {
        if (!form.email || !form.password || form.password.length < 8) {
          toast.error("Email and 8+ char password required");
          setSaving(false);
          return;
        }
        await saApi.post("/admins", form);
        toast.success(`Super admin ${form.email} created`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit super admin" : "New super admin"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="sa-admin-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="sa-admin-form-email" type="email" value={form.email} disabled={isEdit}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>{isEdit ? "New password (leave blank to keep)" : "Temporary password (min 8 chars)"}</Label>
            <div className="relative mt-1.5">
              <Input data-testid="sa-admin-form-password" type={showPw ? "text" : "password"}
                     value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className="pr-10" />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute inset-y-0 right-0 px-3 text-gray-500">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg p-3 border">
            <div>
              <Label className="text-sm font-medium">Master (can manage other super admins)</Label>
              <p className="text-[11px] text-gray-500 mt-0.5">Masters have all features and can create / edit / delete admins.</p>
            </div>
            <Switch data-testid="sa-admin-form-master" checked={form.is_master}
                    onCheckedChange={(v) => setForm({ ...form, is_master: v })} />
          </div>
          {isEdit && (
            <div className="flex items-center justify-between gap-3 rounded-lg p-3 border">
              <div>
                <Label className="text-sm font-medium">Active</Label>
                <p className="text-[11px] text-gray-500 mt-0.5">Disabled admins cannot sign in.</p>
              </div>
              <Switch data-testid="sa-admin-form-active" checked={form.is_active}
                      disabled={isMe}
                      onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          )}

          <div>
            <Label>Tenant scope</Label>
            <p className="text-[11px] text-gray-500 mb-2">Leave all off to grant access to <b>every</b> tenant.</p>
            <div className="max-h-[180px] overflow-y-auto grid grid-cols-2 gap-1.5 rounded-lg border p-3">
              {tenants.map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                         data-testid={`sa-admin-form-tenant-${t.id}`}
                         checked={form.allowed_tenant_ids.includes(t.id)}
                         onChange={() => toggleTenant(t.id)} />
                  <span className="truncate">{t.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Feature scope</Label>
            <p className="text-[11px] text-gray-500 mb-2">Leave all off to grant <b>every</b> feature.</p>
            <div className="grid grid-cols-3 gap-1.5 rounded-lg border p-3">
              {featuresCatalog.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                         data-testid={`sa-admin-form-feature-${f}`}
                         checked={form.allowed_features.includes(f)}
                         onChange={() => toggleFeature(f)} />
                  <span className="text-xs">{f.replace(/_/g, " ")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="sa-admin-form-submit" onClick={submit} disabled={saving}
                  className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save changes" : "Create super admin")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
